import * as _ from 'lodash-es';
import {defer, merge, Observable, of, OperatorFunction, pipe} from 'rxjs';
import {fromArrayLike} from 'rxjs/internal/observable/innerFrom';
import {filter, mergeMap} from 'rxjs/operators';
import {SecurityService} from '../../../components/security';
import {WebSocketMessage} from '../../../components/streaming/streaming-interfaces';
import {SubscriptionSubject} from '../../../components/streaming/subscription-streaming.service';
import {SubscriptionSubjectType} from '../../../components/streaming/subscription-subject-type';
import {ArrayEncodedMdEntry, MdEntryFieldIndex} from '../../../models/common-ui-market-data/array-encoded-md-entry';
import {
    ArrayEncodedMdMessage,
    ArrayEncodedMdSnapshotMessage,
    ArrayEncodedMdUpdateMessage,
    MdMessageFieldIndex
} from '../../../models/common-ui-market-data/array-encoded-md-message';
import {MarketDataEntryType, MarketDataUpdateAction} from '../../../models/common-ui-market-data/market-data.enums';
import {Security} from '../../../models/security';
import {formatSecurityKey, SecurityKey} from '../../../models/security-key';

enum MdWebSocketMessageType {
    MD_SNAPSHOT = 'MD_SNAPSHOT',
    MD_UPDATE   = 'MD_UPDATE'
}

interface MdWebSocketMessage extends WebSocketMessage {
    messageType: MdWebSocketMessageType;
    data: ArrayEncodedMdMessage[];
}

interface MdSnapshotWebSocketMessage extends MdWebSocketMessage {
    messageType: MdWebSocketMessageType.MD_SNAPSHOT;
    data: ArrayEncodedMdSnapshotMessage[];
}

interface MdUpdateWebSocketMessage extends MdWebSocketMessage {
    messageType: MdWebSocketMessageType.MD_UPDATE;
    data: ArrayEncodedMdUpdateMessage[];
}

type MdEntriesDict = Partial<{[mdEntryType in MarketDataEntryType]: ArrayEncodedMdEntry[]}>;

export interface SecurityMarketData {
    mdReqId: string;
    totalTradeVolume: number;
    securityIncrementId: number;
    mdEntriesDict: MdEntriesDict;
    security: Security;
    securityKeyStr: string;
    mdEntryUpdates: ArrayEncodedMdEntry[];
}

export const MD_ENTRY_POSITION_OFFSET = 1;

export class MdSubscriptionSubject {

    private static readonly MD_ENTRY_UPDATE_ACTION_DICT: {
        [action in MarketDataUpdateAction]: (dict: MdEntriesDict, mdEntry: ArrayEncodedMdEntry) => void;
    } = {
        NEW: (mdEntriesDict, mdEntry) => {
            const entryType: MarketDataEntryType = mdEntry[MdEntryFieldIndex.TYPE];
            const typeEntries = mdEntriesDict[entryType];

            if (typeEntries) {
                const newEntryIndex = Math.min(
                    mdEntry[MdEntryFieldIndex.POSITION_NO] - MD_ENTRY_POSITION_OFFSET,
                    typeEntries.length
                );
                typeEntries.splice(newEntryIndex, 0, mdEntry);
            } else {
                mdEntriesDict[entryType] = [mdEntry];
            }
        },
        DELETE: (mdEntriesDict, mdEntry) => {
            const entryType: MarketDataEntryType = mdEntry[MdEntryFieldIndex.TYPE];
            const typeEntries = mdEntriesDict[entryType];

            if (typeEntries) {
                const entryToRemoveId = mdEntry[MdEntryFieldIndex.ID];
                const entryToRemoveIndex = typeEntries.findIndex(
                    currentEntry => currentEntry[MdEntryFieldIndex.ID] === entryToRemoveId
                );

                if (entryToRemoveIndex >= 0) {
                    typeEntries.splice(entryToRemoveIndex, 1);
                }
            }
        },
        CHANGE: (mdEntriesDict, mdEntry) => {
            const entryType: MarketDataEntryType = mdEntry[MdEntryFieldIndex.TYPE];
            const typeEntries = mdEntriesDict[entryType];

            if (typeEntries) {
                const oldEntryId = mdEntry[MdEntryFieldIndex.REF_ID];
                const newEntryIndex = Math.min(
                    mdEntry[MdEntryFieldIndex.POSITION_NO] - MD_ENTRY_POSITION_OFFSET,
                    typeEntries.length
                );

                if (typeEntries[newEntryIndex][MdEntryFieldIndex.ID] === oldEntryId) {
                    typeEntries[newEntryIndex] = mdEntry;
                } else {
                    const oldEntryIndex = typeEntries.findIndex(
                        currentEntry => currentEntry[MdEntryFieldIndex.ID] === oldEntryId
                    );

                    typeEntries.splice(oldEntryIndex, 1);
                    typeEntries.splice(newEntryIndex, 0, mdEntry);
                }
            } else {
                mdEntriesDict[entryType] = [mdEntry];
            }
        }
    };

    public type: SubscriptionSubjectType<SecurityMarketData>;

    protected aggregationSuffix: string;

    private aggregatedMessageTypeToCommonDict: {[aggregatedMessageType: string]: MdWebSocketMessageType};

    private readonly dataBank: {
        [securityKeyStr: string]: SecurityMarketData
    } = {};

    constructor(
        protected aggregationScale: number,
        protected securityService: SecurityService
    ) {
        this.updateAggregationSuffix();
        this.createWebSocketMessageTypes();
        this.createSubjectType();
    }

    protected createSubjectType(): void {
        this.type = {
            name: `MARKET_DATA${this.aggregationSuffix}`,
            isMultiSubject: true,
            operations: {
                SUBSCRIBE: `SUBSCRIBE_MD${this.aggregationSuffix}`,
                UNSUBSCRIBE: `UNSUBSCRIBE_MD${this.aggregationSuffix}`
            },
            messagePipe: pipe(
                filter((webSocketMessage: WebSocketMessage) =>
                    !!this.aggregatedMessageTypeToCommonDict[webSocketMessage.messageType]),

                mergeMap((mdWebSocketMessage: MdWebSocketMessage): Observable<SecurityMarketData> => {
                    const mdMessageContainers: SecurityMarketData[] = this.readMdWebSocketMessage(mdWebSocketMessage);

                    return of(...mdMessageContainers);
                })
            ),
            subjectPipeFactory: subject => this.createSubjectPipe(subject),
            deprecateSubject: subject => this.deprecateSubject(subject)
        };
    }

    protected createWebSocketMessageTypes(): void {
        const snapshotMessage = [MdWebSocketMessageType.MD_SNAPSHOT, this.aggregationSuffix].join('');
        const updateMessage = [MdWebSocketMessageType.MD_UPDATE, this.aggregationSuffix].join('');

        this.aggregatedMessageTypeToCommonDict = {
            [snapshotMessage]: MdWebSocketMessageType.MD_SNAPSHOT,
            [updateMessage]:   MdWebSocketMessageType.MD_UPDATE
        };

        // Support old format of answers without #1
        if (this.aggregationScale === 1 && this.aggregationSuffix) {
            this.aggregatedMessageTypeToCommonDict[MdWebSocketMessageType.MD_SNAPSHOT] =
                MdWebSocketMessageType.MD_SNAPSHOT;
            this.aggregatedMessageTypeToCommonDict[MdWebSocketMessageType.MD_UPDATE] =
                MdWebSocketMessageType.MD_UPDATE;
        }
    }

    protected updateAggregationSuffix(): void {
        this.aggregationSuffix = `#${this.aggregationScale || 1}`;
    }

    protected readMdWebSocketMessage(mdWebSocketMessage: MdWebSocketMessage): SecurityMarketData[] {
        let securitiesMd: SecurityMarketData[];
        const commonMessageType: MdWebSocketMessageType =
                  this.aggregatedMessageTypeToCommonDict[mdWebSocketMessage.messageType];

        switch (commonMessageType) {
            case MdWebSocketMessageType.MD_SNAPSHOT:
                securitiesMd = this.readMdSnapshot(mdWebSocketMessage.data as ArrayEncodedMdSnapshotMessage[]);
                break;
            case MdWebSocketMessageType.MD_UPDATE:
                securitiesMd = this.readMdUpdate(mdWebSocketMessage.data as ArrayEncodedMdUpdateMessage[]);
                break;
        }

        return securitiesMd;
    }

    protected readMdSnapshot(mdMessages: ArrayEncodedMdSnapshotMessage[]): SecurityMarketData[] {
        return mdMessages.map((mdMessage: ArrayEncodedMdSnapshotMessage) => {
            const [mdReqId, securityIncrementId, mdEntries, rawSecurity] = mdMessage;
            const security = this.resolveReceivedSecurity(rawSecurity);
            const securityKeyStr = formatSecurityKey(security);
            const getMdEntryPosNo = (mdEntry: ArrayEncodedMdEntry) => mdEntry[MdEntryFieldIndex.POSITION_NO];

            const mdEntriesDict = mdEntries.reduce((dict, mdEntry) => {
                const entryType = mdEntry[MdEntryFieldIndex.TYPE];
                const typeEntries = dict[entryType];

                if (typeEntries) {

                    // Make sure, that snapshot is ordered by position number
                    const index = _.sortedLastIndexBy(typeEntries, mdEntry, getMdEntryPosNo);

                    typeEntries.splice(index, 0, mdEntry);
                } else {
                    dict[entryType] = [mdEntry];
                }

                return dict;
            }, {} as MdEntriesDict);

            const securityMd: SecurityMarketData = {
                mdReqId,
                totalTradeVolume: 0,
                securityIncrementId,
                mdEntriesDict,
                security,
                securityKeyStr,
                mdEntryUpdates: null
            };

            this.dataBank[securityKeyStr] = securityMd;

            this.securityService.cacheSecurity(security as Security);

            return securityMd;
        });
    }

    protected readMdUpdate(mdMessages: ArrayEncodedMdUpdateMessage[]): SecurityMarketData[] {
        return mdMessages.reduce((mdMessageContainers: SecurityMarketData[], mdMessage) => {
            const securityKey = this.resolveReceivedSecurity(mdMessage[MdMessageFieldIndex.SECURITY_KEY] as Security);
            const securityKeyStr = formatSecurityKey(securityKey);
            const knownSecurityMd: SecurityMarketData = this.dataBank[securityKeyStr];

            if (knownSecurityMd) {
                const [mdReqId, securityIncrementId, mdEntryUpdates] = mdMessage;
                const mdEntriesDict = knownSecurityMd.mdEntriesDict;

                /**
                 * There might be a case when an incremental update follows a snapshot (immediately)
                 * and just repeats the same data. It could cause entries duplication for "NEW" action
                 * in both the snapshot and update.
                 * Check it here.
                 */
                const isSameIncrementId = !!securityIncrementId // Check if API doesn't maintain the securityIncrementId
                    && knownSecurityMd.securityIncrementId === securityIncrementId;

                if (!isSameIncrementId) {
                    knownSecurityMd.mdReqId = mdReqId;
                    knownSecurityMd.securityIncrementId = securityIncrementId;

                    for (const mdEntryUpdate of mdEntryUpdates) {
                        const action = MdSubscriptionSubject
                            .MD_ENTRY_UPDATE_ACTION_DICT[mdEntryUpdate[MdEntryFieldIndex.UPDATE_ACTION]];

                        if (action) {
                            action(mdEntriesDict, mdEntryUpdate);
                        }
                    }

                    knownSecurityMd.mdEntryUpdates = mdEntryUpdates;

                    mdMessageContainers.push(knownSecurityMd);
                }
            }

            return mdMessageContainers;
        }, []);
    }

    protected createSubjectPipe(
        subject: SubscriptionSubject
    ): OperatorFunction<SecurityMarketData, SecurityMarketData> {
        const knownMd$: Observable<SecurityMarketData> = defer(() => {
            const knownMds = subject
                .map<SecurityMarketData>(securityKeyStr => {
                    const knownSecurityMd = this.dataBank[securityKeyStr];

                    return knownSecurityMd && {
                        ...knownSecurityMd,
                        mdEntryUpdates: null // Indicates that the data should be treated as a snapshot.
                    };
                })
                .filter(securityMd => !!securityMd);

            return fromArrayLike(knownMds);
        });

        return pipe(
            filter((securityMd: SecurityMarketData) => subject.includes(securityMd.securityKeyStr)),
            source => merge(knownMd$, source)
        );
    }

    protected deprecateSubject(subject: SubscriptionSubject): void {
        subject.forEach(securityKeyStr => delete this.dataBank[securityKeyStr]);
    }

    protected resolveReceivedSecurity<T extends SecurityKey | Security>(security: T): T {
        return security;
    }

}
